import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../services/apiClient';
import { sequenceService } from '../services/sequenceService';
import { emailService, type EmailTemplate } from '../services/emailService';
import type {
  OutreachSequence,
  SendWindow,
  SequenceChannel,
  SequenceStepInput,
} from '../types/sequence';
import { useToast } from '../contexts/ToastContext';
import { ListPageHeader } from '../components/crm/ListPageHeader';
import { planEnglish } from '../components/sequences/copy';

const DEFAULT_WINDOW: SendWindow = {
  days: [1, 2, 3],
  start: '07:00',
  end: '09:30',
  timezone: 'America/New_York',
};

const WEEKDAYS: { label: string; value: number }[] = [
  { label: 'Mon', value: 0 },
  { label: 'Tue', value: 1 },
  { label: 'Wed', value: 2 },
  { label: 'Thu', value: 3 },
  { label: 'Fri', value: 4 },
  { label: 'Sat', value: 5 },
  { label: 'Sun', value: 6 },
];

const CHANNELS: SequenceChannel[] = ['email', 'call', 'linkedin'];

function blankStep(index: number): SequenceStepInput {
  return {
    step_index: index,
    channel: 'email',
    delay_business_days: index === 0 ? 0 : 2,
    template: null,
    task_title: '',
    is_final: false,
  };
}

const SequenceEditor: React.FC = () => {
  const { sequenceId } = useParams<{ sequenceId: string }>();
  const isEdit = Boolean(sequenceId);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState<SequenceStepInput[]>([blankStep(0)]);
  const [sendWindow, setSendWindow] = useState<SendWindow>(DEFAULT_WINDOW);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [existing, setExisting] = useState<OutreachSequence | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const tmpl = await emailService.listTemplates({ is_active: true });
        if (mounted) setTemplates(tmpl);
        if (isEdit && sequenceId) {
          const seq = await sequenceService.getSequence(sequenceId);
          if (!mounted) return;
          setExisting(seq);
          setName(seq.name);
          setDescription(seq.description);
          setSendWindow(seq.send_window);
          setSteps(
            seq.steps.map((s) => ({
              step_index: s.step_index,
              channel: s.channel,
              delay_business_days: s.delay_business_days,
              template: s.template,
              task_title: s.task_title,
              is_final: s.is_final,
            }))
          );
        }
      } catch (err) {
        showToast(err instanceof ApiError ? err.message : 'Failed to load the sequence.', 'error');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isEdit, sequenceId, showToast]);

  const updateStep = useCallback((index: number, patch: Partial<SequenceStepInput>) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }, []);

  const addStep = useCallback(() => {
    setSteps((prev) => [...prev, blankStep(prev.length)]);
  }, []);

  const removeStep = useCallback((index: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, step_index: i })));
  }, []);

  const moveStep = useCallback((index: number, dir: -1 | 1) => {
    setSteps((prev) => {
      const target = index + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((s, i) => ({ ...s, step_index: i }));
    });
  }, []);

  const toggleDay = useCallback((value: number) => {
    setSendWindow((prev) => {
      const has = prev.days.includes(value);
      const days = has ? prev.days.filter((d) => d !== value) : [...prev.days, value].sort((a, b) => a - b);
      return { ...prev, days };
    });
  }, []);

  const footer = useMemo(() => planEnglish(steps), [steps]);

  const validate = (): string | null => {
    if (!name.trim()) return 'Give the sequence a name.';
    if (steps.length === 0) return 'Add at least one step.';
    if (steps[0].delay_business_days !== 0) return 'The first step must send immediately (0 days).';
    for (const s of steps) {
      if (s.channel === 'email' && !s.template) return 'Every email step needs a template.';
    }
    if (sendWindow.days.length === 0) return 'Pick at least one send day.';
    return null;
  };

  const save = useCallback(async () => {
    const problem = validate();
    if (problem) {
      showToast(problem, 'error');
      return;
    }
    setSaving(true);
    const payload = {
      name: name.trim(),
      description: description.trim(),
      send_window: sendWindow,
      steps: steps.map((s, i) => ({
        step_index: i,
        channel: s.channel,
        delay_business_days: i === 0 ? 0 : s.delay_business_days,
        template: s.channel === 'email' ? s.template : null,
        task_title: s.channel === 'email' ? '' : s.task_title,
        is_final: s.is_final,
      })),
    };
    try {
      if (isEdit && sequenceId) {
        await sequenceService.updateSequence(sequenceId, payload);
        showToast('Sequence saved', 'success');
      } else {
        await sequenceService.createSequence(payload);
        showToast('Sequence created', 'success');
      }
      navigate('/sequences');
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not save the sequence.', 'error');
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, description, sendWindow, steps, isEdit, sequenceId, navigate, showToast]);

  if (loading) {
    return <div className="p-6 text-sm text-fg-muted">Loading…</div>;
  }

  const activeCount = existing?.active_enrollment_count ?? 0;

  return (
    <div className="space-y-4">
      <ListPageHeader
        title={isEdit ? 'Edit sequence' : 'New sequence'}
        subtitle="A planned series of touches that stops the moment someone replies."
        actions={
          <Link to="/sequences" className="btn-secondary rounded-xl px-4 py-2 text-sm">
            Cancel
          </Link>
        }
      />

      {isEdit && activeCount > 0 ? (
        <div className="rounded-xl border border-border bg-surface-muted p-3 text-sm text-fg-muted">
          Saving creates version {(existing?.version ?? 1) + 1}. The {activeCount}{' '}
          {activeCount === 1 ? 'person' : 'people'} currently in this sequence stay on version{' '}
          {existing?.version}.
        </div>
      ) : null}

      <div className="rounded-xl border border-border bg-surface p-4 space-y-3">
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-fg">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. New lead follow-up"
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-fg">Description</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg"
          />
        </label>
      </div>

      <div className="space-y-3">
        {steps.map((step, index) => (
          <div key={index} className="rounded-xl border border-border bg-surface p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
                Step {index + 1}
                {index === 0 ? ' · sends immediately' : ''}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label="Move up"
                  disabled={index === 0}
                  onClick={() => moveStep(index, -1)}
                  className="btn-surface-muted rounded-lg px-2 py-1 text-xs disabled:opacity-40"
                >
                  ↑
                </button>
                <button
                  type="button"
                  aria-label="Move down"
                  disabled={index === steps.length - 1}
                  onClick={() => moveStep(index, 1)}
                  className="btn-surface-muted rounded-lg px-2 py-1 text-xs disabled:opacity-40"
                >
                  ↓
                </button>
                <button
                  type="button"
                  aria-label="Delete step"
                  disabled={steps.length === 1}
                  onClick={() => removeStep(index)}
                  className="btn-surface-muted rounded-lg px-2 py-1 text-xs disabled:opacity-40"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <label className="block text-sm">
                <span className="mb-1 block text-xs text-fg-muted">Channel</span>
                <select
                  value={step.channel}
                  onChange={(e) => updateStep(index, { channel: e.target.value as SequenceChannel })}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg"
                >
                  {CHANNELS.map((c) => (
                    <option key={c} value={c}>
                      {c === 'email' ? 'Email' : c === 'call' ? 'Call' : 'LinkedIn'}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-sm">
                <span className="mb-1 block text-xs text-fg-muted">Business days after previous</span>
                <input
                  type="number"
                  min={0}
                  disabled={index === 0}
                  value={index === 0 ? 0 : step.delay_business_days}
                  onChange={(e) => updateStep(index, { delay_business_days: Math.max(0, Number(e.target.value)) })}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg disabled:opacity-50"
                />
              </label>

              {step.channel === 'email' ? (
                <label className="block text-sm">
                  <span className="mb-1 block text-xs text-fg-muted">Template</span>
                  <select
                    value={step.template ?? ''}
                    onChange={(e) => updateStep(index, { template: e.target.value || null })}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg"
                  >
                    <option value="">Choose a template…</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <label className="block text-sm">
                  <span className="mb-1 block text-xs text-fg-muted">Task title</span>
                  <input
                    value={step.task_title}
                    onChange={(e) => updateStep(index, { task_title: e.target.value })}
                    placeholder="e.g. Call prospect"
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg"
                  />
                </label>
              )}
            </div>

            <label className="mt-3 flex items-center gap-2 text-sm text-fg">
              <input
                type="checkbox"
                checked={step.is_final}
                onChange={(e) => updateStep(index, { is_final: e.target.checked })}
              />
              Final touch (the breakup message)
            </label>
          </div>
        ))}

        <button type="button" onClick={addStep} className="btn-secondary rounded-xl px-4 py-2 text-sm">
          Add a step
        </button>
      </div>

      <div className="rounded-xl border border-border bg-surface p-4">
        <p className="mb-2 text-sm font-medium text-fg">Send window</p>
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map((d) => {
            const on = sendWindow.days.includes(d.value);
            return (
              <button
                key={d.value}
                type="button"
                onClick={() => toggleDay(d.value)}
                className={`rounded-lg border px-3 py-1 text-sm ${
                  on ? 'border-primary bg-primary/10 text-primary' : 'border-border text-fg-muted'
                }`}
              >
                {d.label}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-fg-muted">From</span>
            <input
              type="time"
              value={sendWindow.start}
              onChange={(e) => setSendWindow((p) => ({ ...p, start: e.target.value }))}
              className="rounded-lg border border-border bg-surface px-2 py-1 text-fg"
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-fg-muted">to</span>
            <input
              type="time"
              value={sendWindow.end}
              onChange={(e) => setSendWindow((p) => ({ ...p, end: e.target.value }))}
              className="rounded-lg border border-border bg-surface px-2 py-1 text-fg"
            />
          </label>
        </div>
        <p className="mt-2 text-xs text-fg-muted">Steps outside this window move to the next opening.</p>
      </div>

      <div className="flex items-center justify-between rounded-xl border border-border bg-surface-muted p-4">
        <p className="text-sm text-fg">{footer}</p>
        <button type="button" onClick={save} disabled={saving} className="btn-primary rounded-xl px-5 py-2 text-sm disabled:opacity-50">
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create sequence'}
        </button>
      </div>
    </div>
  );
};

export default SequenceEditor;
